import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../Navbar";
import { UrlApi } from "../../services/apirest";
import { Button, Container, Stack, TextField, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import { createTheme, ThemeProvider, useTheme } from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddBoxTwoToneIcon from "@mui/icons-material/AddBoxTwoTone";
import { logEventos } from "../../services/services";

export default function IngresoFacturasBanco() {
  const [facturasBanco, setFacturasBanco] = useState([]);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const globalTheme = useTheme();

  const obtenerFacturasBanco = () => {
    fetch(`${UrlApi}/banco/facturas`)
      .then((data) => data.json())
      .then((data) => setFacturasBanco(Array.isArray(data) ? data : []))
      .catch(() => setFacturasBanco([]));
  };

  useEffect(() => {
    obtenerFacturasBanco();
  }, []);

  useEffect(() => {
    logEventos(
      "Acceso al menu Ingreso Facturas Banco",
      "Banco",
      "Ingreso Facturas",
      localStorage.getItem("user")
    );
  }, []);

  const columns = useMemo(
    () => [
      { header: "Banco", accessorKey: "banco", size: 180 },
      { header: "N° Prestamo", accessorKey: "nro_prestamo", size: 150 },
      { header: "Cuota", accessorKey: "cuota", size: 90 },
      {
        header: "Capital",
        accessorFn: (f) => formatearImporte(f.capital),
        size: 110,
        muiTableBodyCellProps: { align: "right" },
      },
      {
        header: "Interes",
        accessorFn: (f) => formatearImporte(f.interes),
        size: 110,
        muiTableBodyCellProps: { align: "right" },
      },
      {
        header: "IVA",
        accessorFn: (f) => formatearImporte(f.iva),
        size: 110,
        muiTableBodyCellProps: { align: "right" },
      },
      {
        header: "Otros gastos",
        accessorFn: (f) => formatearImporte(f.otros_gastos),
        size: 130,
        muiTableBodyCellProps: { align: "right" },
      },
    ],
    []
  );

  const facturasFiltradas = useMemo(() => {
    const inicio = fechaDesde ? new Date(`${fechaDesde}T00:00:00`) : null;
    const fin = fechaHasta ? new Date(`${fechaHasta}T23:59:59`) : null;

    return facturasBanco.filter((factura) => {
      if (!inicio && !fin) return true;
      const fechaRaw = factura.fecha_factura || factura.fecha || factura.createdAt;
      if (!fechaRaw) return true;
      const fecha = new Date(fechaRaw);
      if (Number.isNaN(fecha.getTime())) return true;
      if (inicio && fecha < inicio) return false;
      if (fin && fecha > fin) return false;
      return true;
    });
  }, [facturasBanco, fechaDesde, fechaHasta]);

  const muiSearchTextFieldProps = {
    placeholder: "Buscar..",
    sx: { minWidth: "18rem", backgroundColor: "#ffffff" },
    variant: "outlined",
    color: "primary",
    size: "small",
  };

  const initialState = {
    density: "compact",
    showGlobalFilter: true,
    showColumnFilters: true,
    pagination: {
      pageSize: 15,
      pageIndex: 0,
    },
  };

  const tableHeadCellProps = {
    align: "center",
    sx: {
      fontSize: 12,
      color: "white",
      fontWeight: 600,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      background: "linear-gradient(180deg, #1e3a5f 0%, #17304f 100%)",
      borderRight: "1px solid rgba(255,255,255,0.07)",
      py: 1.5,
      "&:last-child": { borderRight: "none" },
    },
  };

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: globalTheme.palette.mode,
          primary: globalTheme.palette.primary,
          info: {
            main: "rgb(255,122,0)",
          },
          background: {
            default:
              globalTheme.palette.mode === "light" ? "#f0f4f8" : "#0a0f1a",
          },
        },
        typography: {
          button: {
            textTransform: "none",
            fontSize: "0.85rem",
            fontWeight: 500,
          },
        },
        components: {
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontSize: "0.75rem",
              },
            },
          },
        },
      }),
    [globalTheme]
  );

  return (
    <div>
      <Navbar />
      <Container maxWidth="xl">
        <Box mt={2} boxShadow={3} mb={4}>
          <ThemeProvider theme={tableTheme}>
            <MaterialReactTable
              enableColumnResizing
              columns={columns}
              data={facturasFiltradas}
              initialState={initialState}
              positionActionsColumn="last"
              enableExpandAll={false}
              enableToolbarInternalActions={true}
              muiTableHeadCellProps={tableHeadCellProps}
              localization={MRT_Localization_ES}
              muiTableBodyCellProps={{
                align: "left",
                sx: {
                  color: "#1e293b",
                  borderColor: "#f1f5f9",
                  fontSize: "0.82rem",
                  py: 0.65,
                },
              }}
              muiTableBodyRowProps={({ row }) => ({
                sx: {
                  backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#f9fbfd",
                  transition: "background-color 0.15s ease",
                  "&:hover td": {
                    backgroundColor: "#eff6ff !important",
                  },
                },
              })}
              muiTablePaperProps={{
                sx: {
                  borderRadius: 0,
                  boxShadow: "none",
                  border: "none",
                  backgroundColor: "transparent",
                },
              }}
              muiTopToolbarProps={{
                sx: {
                  backgroundColor: "#ffffff",
                  borderBottom: "1px solid #f1f5f9",
                  px: { xs: 1.5, sm: 2 },
                  py: 1.25,
                  flexWrap: "wrap",
                  gap: 1,
                },
              }}
              muiBottomToolbarProps={{
                sx: {
                  backgroundColor: "#f8fafc",
                  borderTop: "1px solid #e8eef4",
                  minHeight: 52,
                },
              }}
              muiTableContainerProps={{
                sx: { maxHeight: "calc(100vh - 270px)" },
              }}
              enableColumnActions={false}
              muiSearchTextFieldProps={{
                ...muiSearchTextFieldProps,
                placeholder: "Buscar…",
                sx: {
                  minWidth: 200,
                  "& .MuiInputBase-root": {
                    borderRadius: "8px",
                    backgroundColor: "#f8fafc",
                    fontSize: "0.83rem",
                  },
                },
              }}
              enableDensityToggle={false}
              renderTopToolbarCustomActions={() => (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddBoxTwoToneIcon />}
                  >
                    Nuevo Comprobante
                  </Button>
                  <Typography variant="poster" fontSize={26}>
                    <AssignmentIcon sx={{ fontSize: 20 }} />
                    Ingreso Facturas Banco
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2 }}>
                    <TextField
                      label="Fecha desde"
                      type="date"
                      size="small"
                      value={fechaDesde}
                      onChange={(e) => setFechaDesde(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ backgroundColor: "#fff", minWidth: 150 }}
                    />
                    <TextField
                      label="Fecha hasta"
                      type="date"
                      size="small"
                      value={fechaHasta}
                      onChange={(e) => setFechaHasta(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ backgroundColor: "#fff", minWidth: 150 }}
                    />
                    {(fechaDesde || fechaHasta) && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          setFechaDesde("");
                          setFechaHasta("");
                        }}
                      >
                        Limpiar
                      </Button>
                    )}
                  </Stack>
                </>
              )}
              layoutMode="grid"
              enableRowActions={false}
              enableColumnDragging={false}
              enableColumnFilters={true}
              enableHiding={false}
              positionExpandColumn="first"
            />
          </ThemeProvider>
        </Box>
      </Container>
    </div>
  );
}

function formatearImporte(valor) {
  if (valor === null || valor === undefined || valor === "") return "-";
  const numero = Number(valor);
  if (Number.isNaN(numero)) return valor;
  return new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numero);
}
